import { cn } from '@/lib/utils';
import { APIProvider, Map, AdvancedMarker } from '@vis.gl/react-google-maps';
import type { MapMouseEvent } from '@vis.gl/react-google-maps';
import { MapPin } from 'lucide-react';
import { useCallback, useId, useMemo, useRef } from 'react';

interface LocationPickerProps {
  latitude: string;
  longitude: string;
  onLocationChange: (lat: string, lng: string) => void;
  googleMapsApiKey?: string;
  className?: string;
  height?: string;
}

const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 };
const DEFAULT_ZOOM = 4;
const PIN_ZOOM = 15;

function LocationPickerMap({
  latitude,
  longitude,
  onLocationChange,
  className,
  height = 'h-64',
}: Omit<LocationPickerProps, 'googleMapsApiKey'>) {
  const mapId = useId();
  const hasPin = latitude !== '' && longitude !== '' && !isNaN(Number(latitude)) && !isNaN(Number(longitude));
  const pinLat = hasPin ? parseFloat(latitude) : 0;
  const pinLng = hasPin ? parseFloat(longitude) : 0;

  const initialCenter = useRef(hasPin ? { lat: pinLat, lng: pinLng } : DEFAULT_CENTER);
  const initialZoom = useRef(hasPin ? PIN_ZOOM : DEFAULT_ZOOM);

  const handleMapClick = useCallback(
    (e: MapMouseEvent) => {
      const latLng = e.detail.latLng;
      if (!latLng) return;
      onLocationChange(latLng.lat.toFixed(6), latLng.lng.toFixed(6));
    },
    [onLocationChange],
  );

  const handleDragEnd = useCallback(
    (e: google.maps.MapMouseEvent) => {
      const latLng = e.latLng;
      if (!latLng) return;
      onLocationChange(latLng.lat().toFixed(6), latLng.lng().toFixed(6));
    },
    [onLocationChange],
  );

  const markerPosition = useMemo(
    () => (hasPin ? { lat: pinLat, lng: pinLng } : null),
    [hasPin, pinLat, pinLng],
  );

  return (
    <div className={cn('relative rounded-lg overflow-hidden border', height, className)}>
      <Map
        mapId={mapId}
        defaultCenter={initialCenter.current}
        defaultZoom={initialZoom.current}
        gestureHandling="cooperative"
        disableDefaultUI={false}
        zoomControl={true}
        mapTypeControl={false}
        streetViewControl={false}
        fullscreenControl={false}
        onClick={handleMapClick}
        className="w-full h-full"
      >
        {markerPosition && (
          <AdvancedMarker position={markerPosition} draggable onDragEnd={handleDragEnd} />
        )}
      </Map>
      {!hasPin && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="bg-background/90 backdrop-blur-sm rounded-lg px-4 py-2 shadow-sm border">
            <p className="text-sm text-muted-foreground font-medium">Click the map to place a pin</p>
          </div>
        </div>
      )}
    </div>
  );
}

export function LocationPicker({
  googleMapsApiKey,
  ...props
}: LocationPickerProps) {
  if (!googleMapsApiKey) {
    return (
      <div
        className={cn(
          'rounded-lg bg-muted/50 flex flex-col items-center justify-center text-muted-foreground',
          props.height || 'h-64',
          props.className,
        )}
      >
        <MapPin className="h-6 w-6 mb-1" />
        <span className="text-sm">Map unavailable — no API key configured</span>
      </div>
    );
  }

  return (
    <APIProvider apiKey={googleMapsApiKey}>
      <LocationPickerMap {...props} />
    </APIProvider>
  );
}
